import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL as string | undefined,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string | undefined,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string | undefined,
}

const apiKey = (firebaseConfig.apiKey || '').trim()
const hasValidConfig =
  apiKey.length > 20 &&
  !apiKey.includes('your_') &&
  Boolean(firebaseConfig.authDomain && firebaseConfig.projectId)

let app: FirebaseApp | undefined
let auth: Auth | null = null

if (hasValidConfig) {
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp()
  auth = getAuth(app)
} else if (import.meta.env.DEV) {
  console.warn(
    '[Firebase] Missing or invalid VITE_FIREBASE_API_KEY. Add Firebase keys to frontend-app/.env.local and restart Vite. Phone OTP / Google login will stay disabled until then.',
  )
}

if (typeof window !== 'undefined' && hasValidConfig) {
  const onAdmin = window.location.pathname.startsWith('/admin-new')
  if (
    !onAdmin &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    console.info(`[Firebase] Phone OTP host: ${window.location.hostname} — must be in Firebase authorized domains.`)
  }
}

let analytics: Analytics | null = null
if (typeof window !== 'undefined' && app && hasValidConfig) {
  const firebaseApp = app
  isSupported()
    .then((supported) => {
      if (supported) analytics = getAnalytics(firebaseApp)
    })
    .catch(() => {})
}

export { auth, app, analytics }

export function requireAuth(): Auth {
  if (!auth) {
    throw new Error(
      'Firebase is not configured. Add VITE_FIREBASE_* keys to frontend-app/.env.local and restart Vite.',
    )
  }
  return auth
}

export function isFirebaseReady(): boolean {
  return Boolean(auth)
}
